import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Card,
  CardContent,
  IconButton,
  Button,
  Stack,
  alpha,
  useTheme,
  Alert,
  Grid,
  Avatar,
  Tabs,
  Tab,
  Fade,
  Zoom,
  Slide,
  useScrollTrigger,
  useMediaQuery,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  PrivacyTip as PrivacyIcon,
  Description as TermsIcon,
  CurrencyExchange as RefundIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckIcon,
  Cookie as CookieIcon,
  Copyright as CopyrightIcon,
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  Gavel as GavelIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Shield as ShieldIcon,
  Verified as VerifiedIcon,
  Update as UpdateIcon,
  Payment as PaymentIcon,
  CancelPresentation as CancelIcon,
  Help as HelpIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Favorite as FavoriteIcon,
  Star as StarIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
    Link as LinkIcon,
    Web as WebIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import logo from "../assets/Images/Clanbridge-logo.png2.png";


const GlassCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.95)} 0%, ${alpha(
    theme.palette.common.white,
    0.98
  )} 100%)`,
  backdropFilter: 'blur(10px)',
  borderRadius: '28px',
  border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
  boxShadow: '0 20px 40px -12px rgba(0,0,0,0.1)',
  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 30px 50px -12px rgba(0,0,0,0.2)',
  },
}));

const GradientText = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  color: 'transparent',
  fontWeight: 800,
}));

const FloatingIcon = styled(Avatar)(({ theme, bgcolor }) => ({
  background: bgcolor || `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  width: 56,
  height: 56,
  boxShadow: '0 8px 16px -4px rgba(0,0,0,0.15)',
}));

const InteractiveCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '24px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  background: theme.palette.common.white,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: '0 12px 28px -8px rgba(0,0,0,0.15)',
  },
}));

const NumberBadge = styled(Box)(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: '12px',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: '1.25rem',
}));

const MobileNavDrawer = styled(Paper)(({ theme, open }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1200,
  transform: open ? 'translateX(0)' : 'translateX(-100%)',
  transition: 'transform 0.3s ease-in-out',
  borderRadius: 0,
  overflowY: 'auto',
  padding: theme.spacing(3),
  background: alpha(theme.palette.common.white, 0.98),
  backdropFilter: 'blur(20px)',
}));


function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

export default function LegalDocumentation() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [tabValue, setTabValue] = React.useState(0);
  const navigate = useNavigate();

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handlePrint = () => {
    window.print();
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };


  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e9edf2 100%)',
      position: 'relative',
      overflowX: 'hidden'
    }}>

      <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', zIndex: 0 }}>
        {[...Array(12)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: Math.random() * 200 + 50,
              height: Math.random() * 200 + 50,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.03)} 0%, transparent 70%)`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `float ${Math.random() * 10 + 10}s infinite ease-in-out`,
            }}
          />
        ))}
      </Box>

      <Slide direction="down" in={!trigger} mountOnEnter unmountOnExit>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            background: alpha(theme.palette.common.white, 0.9),
            backdropFilter: 'blur(20px)',
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            zIndex: 1100,
          }}
        >
                  <Toolbar sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                          <Stack direction="row" spacing={2} alignItems="center">
                              <IconButton onClick={() => window.history.back()} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                                  <ArrowBackIcon />
                              </IconButton>
                              <Box>
                                  <img
                                      src={logo}
                                      alt="logo"
                                      className="loginLogo"
                                      style={{ width: "100%", maxWidth: "170px" }}
                                  />
                                  <Typography variant="caption" color="text.secondary" display={{ xs: 'none', sm: 'block' }}>
                                      Your trusted legal documentation
                                  </Typography>

                              </Box>
                          </Stack>
                          {isMobile ? (
                              <IconButton sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                                  <MenuIcon />
                              </IconButton>
                          ) : (
                              <Stack direction="row" spacing={1}>
                                  <IconButton onClick={handlePrint} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                                      <PrintIcon />
                                  </IconButton>
                                  <IconButton sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                                      <ShareIcon />
                                  </IconButton>
                                  <Button
                                      variant="contained"
                                      startIcon={<HomeIcon />}
                                      onClick={() => navigate('/')}
                                      sx={{ borderRadius: '40px', textTransform: 'none' }}
                                  >
                                      Home
                                  </Button>
                              </Stack>
                          )}
                      </Stack>
                  </Toolbar>
        </AppBar>
      </Slide>

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, pt: { xs: 1, sm: 2, md: 3 }, pb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>

        <Zoom in timeout={800}>
          <Box textAlign="center" mb={{ xs: 4, sm: 5, md: 6 }}>
            <Chip
              icon={<VerifiedIcon />}
              label="Legally Compliant"
              sx={{ mb: { xs: 2, sm: 2 }, bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}
            />
            <GradientText variant="h3" sx={{ fontSize: { xs: '1rem', sm: '2rem', md: '3rem' }, fontWeight: 800, mb: 1 }}>
              Legal Information
            </GradientText>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' }, maxWidth: 600, mx: 'auto', mb: 3, px: 2 }}>
              Please read our terms, policies, and conditions carefully before using our services
            </Typography>
           
          </Box>
        </Zoom>

        <Paper elevation={0} sx={{ borderRadius: { xs: '20px', sm: '28px' }, overflow: 'hidden', bgcolor: 'transparent' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant={isMobile ? "fullWidth" : "standard"}
            centered={!isMobile}
            sx={{
              bgcolor: alpha(theme.palette.common.white, 0.9),
              backdropFilter: 'blur(10px)',
              borderRadius: { xs: '20px 20px 0 0', sm: '28px 28px 0 0' },
              '& .MuiTab-root': { 
                py: { xs: 1.5, sm: 2 }, 
                fontSize: { xs: '0.75rem', sm: '0.9rem', md: '1rem' }, 
                fontWeight: 600,
                minWidth: { xs: 'auto', sm: 120 },
                px: { xs: 1, sm: 2 }
              },
            }}
          >
            <Tab icon={<GavelIcon />} label="Terms & Conditions" />
            <Tab icon={<ShieldIcon />} label="Privacy Policy" />
            <Tab icon={<PaymentIcon />} label="Refund & Cancellation Policy" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Box id="welcome" sx={{ scrollMarginTop: '100px' }}>
              <Slide in direction="up">
                <GlassCard>
                  <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }} mb={2}>
                      <NumberBadge sx={{ width: { xs: 28, sm: 36 }, height: { xs: 28, sm: 36 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}>1</NumberBadge>
                      <Typography variant="h4" fontWeight={700} fontSize={{ xs: '1.4rem', sm: '1.7rem', md: '2rem' }}>
                        Welcome to Clanbridge!
                      </Typography>
                    </Stack>
                    <Typography variant="body1" paragraph fontSize={{ xs: '0.9rem', sm: '1rem', md: '1.1rem' }} lineHeight={1.6}>
                      These terms and conditions outline the rules and regulations for the use of Clanbridge Living 
                      Private Limited's Website, located at <strong>www.clanbridge.com</strong>.
                    </Typography>
                    <Alert 
                      severity="info" 
                      icon={<VerifiedIcon />}
                      sx={{ borderRadius: '20px', bgcolor: alpha(theme.palette.info.main, 0.05) }}
                    >
                      By accessing this website we assume you accept these terms and conditions. Do not continue to use 
                      Clanbridge if you do not agree to take all of the terms and conditions stated on this page.
                    </Alert>
                  </CardContent>
                </GlassCard>
              </Slide>
            </Box>

            <Box id="terms" sx={{ scrollMarginTop: '100px' }} mt={{ xs: 3, sm: 4 }}>

              <Slide in direction="up" timeout={500}>
                <GlassCard sx={{ mb: { xs: 2, sm: 3 } }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }} mb={{ xs: 2, sm: 3 }}>
                      <FloatingIcon sx={{ width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
                        <AssignmentIcon />
                      </FloatingIcon>
                      <Typography variant="h5" fontWeight={700} fontSize={{ xs: '1.25rem', sm: '1.5rem' }}>
                        Terminology
                      </Typography>
                    </Stack>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }}>
                      The following terminology applies to these Terms and Conditions, Privacy Statement and Disclaimer Notice and all Agreements: “Client”, “You” and “Your” refers to you, the person log on this website and compliant to the Company’s terms and conditions. “The Company”, “Ourselves”, “We”, “Our” and “Us”, refers to our Company. “Party”, “Parties”, or “Us”, refers to both the Client and ourselves. All terms refer to the offer, acceptance and consideration of payment necessary to undertake the process of our assistance to the Client in the most appropriate manner for the express purpose of meeting the Client’s needs in respect of provision of the Company’s stated services, in accordance with and subject to, prevailing law of India. Any use of the above terminology or other words in the singular, plural, capitalization and/or he/she or they, are taken as interchangeable and therefore as referring to same.
                    </Typography>
                  </CardContent>
                </GlassCard>
              </Slide>

              <Slide in direction="up" timeout={600}>
                <GlassCard sx={{ mb: { xs: 2, sm: 3 } }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }} mb={{ xs: 2, sm: 3 }}>
                      <FloatingIcon sx={{ width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
                        <CookieIcon />
                      </FloatingIcon>
                      <Typography variant="h5" fontWeight={700} fontSize={{ xs: '1.25rem', sm: '1.5rem' }}>
                        Cookies
                      </Typography>
                    </Stack>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} paragraph>
                      We employ the use of cookies. By accessing Clanbridge, you agreed to use cookies in agreement with the Clanbridge Living Private Limited's Privacy Policy.
                    </Typography>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }}>
                      Most interactive websites use cookies to let us retrieve the user's details for each visit. Cookies are used by our website to enable the functionality of certain areas to make it easier for people visiting our website. Some of our affiliate/advertising partners may also use cookies.
                    </Typography>
                  </CardContent>
                </GlassCard>
              </Slide>

              <Slide in direction="up" timeout={700}>
                <GlassCard sx={{ mb: { xs: 2, sm: 3 } }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }} mb={{ xs: 2, sm: 3 }}>
                      <FloatingIcon sx={{ width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
                        <CopyrightIcon />
                      </FloatingIcon>
                      <Typography variant="h5" fontWeight={700} fontSize={{ xs: '1.25rem', sm: '1.5rem' }}>
                        License
                      </Typography>
                    </Stack>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} paragraph>
                      Unless otherwise stated, Clanbridge Living Private Limited and/or its licensors own the intellectual property rights for all material on Clanbridge. All intellectual property rights are reserved. You may access this from Clanbridge for your own personal use subjected to restrictions set in these terms and conditions.
                    </Typography>
                    <Box mt={3}>
                      <Typography variant="h6" color="error" fontWeight={600} gutterBottom fontSize={{ xs: '1rem', sm: '1.125rem' }}>
                        You Must Not:
                      </Typography>
                      <Grid container spacing={{ xs: 1, sm: 2 }}>
                        {[
                          'Republish material from Clanbridge',
                          'Sell, rent or sub-license material from Clanbridge',
                          'Reproduce, duplicate or copy material from Clanbridge',
                          'Redistribute content from Clanbridge'
                        ].map((item, i) => (
                          <Grid item xs={12} sm={6} key={i}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <WarningIcon sx={{ color: theme.palette.error.main, fontSize: { xs: 16, sm: 20 } }} />
                              <Typography variant="body2" fontSize={{ xs: '0.75rem', sm: '0.875rem' }}>{item}</Typography>
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} sx={{ mt: 2 }}>
                      This Agreement shall begin on the date hereof.
                    </Typography>
                  </CardContent>
                </GlassCard>
              </Slide>

              <Slide in direction="up" timeout={800}>
                <GlassCard sx={{ mb: { xs: 2, sm: 3 } }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }} mb={{ xs: 2, sm: 3 }}>
                      <FloatingIcon sx={{ width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
                        <ChatIcon />
                      </FloatingIcon>
                      <Typography variant="h5" fontWeight={700} fontSize={{ xs: '1.25rem', sm: '1.5rem' }}>
                        User Comments
                      </Typography>
                    </Stack>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} paragraph>
                      Parts of this website offer an opportunity for users to post and exchange opinions and information in certain areas of the website. Clanbridge Living Private Limited does not filter, edit, publish or review Comments prior to their presence on the website. Comments do not reflect the views and opinions of Clanbridge Living Private Limited, its agents and/or affiliates. Comments reflect the views and opinions of the person who post their views and opinions. To the extent permitted by applicable laws, Clanbridge Living Private Limited shall not be liable for the Comments or for any liability, damages or expenses caused and/or suffered as a result of any use of and/or posting of and/or appearance of the Comments on this website.
                    </Typography>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} paragraph>
                      Clanbridge Living Private Limited reserves the right to monitor all Comments and to remove any Comments which can be considered inappropriate, offensive or causes breach of these Terms and Conditions.
                    </Typography>
                    <Box mt={3}>
                      <Typography variant="h6" fontWeight={600} gutterBottom fontSize={{ xs: '1rem', sm: '1.125rem' }}>
                        You Warrant and Represent that:
                      </Typography>
                      <Grid container spacing={{ xs: 1, sm: 2 }}>
                        {[
                          'You are entitled to post the Comments on our website and have all necessary licenses and consents to do so',
                          'The Comments do not invade any intellectual property right, including without limitation copyright, patent or trademark of any third party',
                          'The Comments do not contain any defamatory, libelous, offensive, indecent or otherwise unlawful material which is an invasion of privacy',
                          'The Comments will not be used to solicit or promote business or custom or present commercial activities or unlawful activity'
                        ].map((item, i) => (
                          <Grid item xs={12} key={i}>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                              <CheckIcon sx={{ color: theme.palette.success.main, fontSize: { xs: 16, sm: 20 }, mt: 0.5 }} />
                              <Typography variant="body2" fontSize={{ xs: '0.75rem', sm: '0.875rem' }}>{item}</Typography>
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>
                      <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} sx={{ mt: 2 }}>
                        You hereby grant Clanbridge Living Private Limited a non-exclusive license to use, reproduce, edit and authorize others to use, reproduce and edit any of your Comments in any and all forms, formats or media.
                      </Typography>
                    </Box>
                  </CardContent>
                </GlassCard>
              </Slide>

              <Slide in direction="up" timeout={900}>
                <GlassCard sx={{ mb: { xs: 2, sm: 3 } }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }} mb={{ xs: 2, sm: 3 }}>
                      <FloatingIcon sx={{ width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
                        <LinkIcon />
                      </FloatingIcon>
                      <Typography variant="h5" fontWeight={700} fontSize={{ xs: '1.25rem', sm: '1.5rem' }}>
                        Hyperlinking to Our Content
                      </Typography>
                    </Stack>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} paragraph>
                      The following organizations may link to our Website without prior written approval:
                    </Typography>
                    <List dense sx={{ mb: 2 }}>
                      {['Government agencies', 'Search engines', 'News organizations', 'Online directory distributors may link to our Website in the same manner as they hyperlink to the Websites of other listed businesses', 'System wide Accredited Businesses except soliciting non-profit organizations, charity shopping malls, and charity fundraising groups which may not hyperlink to our Web site'].map((item, idx) => (
                        <ListItem key={idx} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}><CheckIcon fontSize="small" color="primary" /></ListItemIcon>
                          <ListItemText primary={item} primaryTypographyProps={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }} />
                        </ListItem>
                      ))}
                    </List>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} paragraph>
                      These organizations may link to our home page, to publications or to other Website information so long as the link: (a) is not in any way deceptive; (b) does not falsely imply sponsorship, endorsement or approval of the linking party and its products and/or services; and (c) fits within the context of the linking party’s site.
                    </Typography>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} paragraph>
                      We may consider and approve other link requests from the following types of organizations:
                    </Typography>
                    <List dense sx={{ mb: 2 }}>
                      {['commonly-known consumer and/or business information sources', 'dot.com community sites', 'associations or other groups representing charities', 'online directory distributors', 'internet portals', 'accounting, law and consulting firms', 'educational institutions and trade associations'].map((item, idx) => (
                        <ListItem key={idx} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}><CheckIcon fontSize="small" color="primary" /></ListItemIcon>
                          <ListItemText primary={item} primaryTypographyProps={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }} />
                        </ListItem>
                      ))}
                    </List>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} paragraph>
                      We will approve link requests from these organizations if we decide that: (a) the link would not make us look unfavorably to ourselves or to our accredited businesses; (b) the organization does not have any negative records with us; (c) the benefit to us from the visibility of the hyperlink compensates the absence of Clanbridge Living Private Limited; and (d) the link is in the context of general resource information.
                    </Typography>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} paragraph>
                      These organizations may link to our home page so long as the link: (a) is not in any way deceptive; (b) does not falsely imply sponsorship, endorsement or approval of the linking party and its products or services; and (c) fits within the context of the linking party’s site.
                    </Typography>
                    <Alert severity="info" sx={{ my: 2, borderRadius: '20px' }}>
                      If you are one of the organizations listed in paragraph 2 above and are interested in linking to our website, you must inform us by sending an e-mail to Clanbridge Living Private Limited. Please include your name, your organization name, contact information as well as the URL of your site, a list of any URLs from which you intend to link to our Website, and a list of the URLs on our site to which you would like to link. Wait 2-3 weeks for a response.
                    </Alert>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} paragraph>
                      Approved organizations may hyperlink to our Website as follows:
                    </Typography>
                    <List dense>
                      {['By use of our corporate name', 'By use of the uniform resource locator being linked to', 'By use of any other description of our Website being linked to that makes sense within the context and format of content on the linking party’s site'].map((item, idx) => (
                        <ListItem key={idx} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}><CheckIcon fontSize="small" color="primary" /></ListItemIcon>
                          <ListItemText primary={item} primaryTypographyProps={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }} />
                        </ListItem>
                      ))}
                    </List>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }}>
                      No use of Clanbridge Living Private Limited’s logo or other artwork will be allowed for linking absent a trademark license agreement.
                    </Typography>
                  </CardContent>
                </GlassCard>
              </Slide>

              <Slide in direction="up" timeout={1000}>
                <GlassCard sx={{ mb: { xs: 2, sm: 3 } }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }} mb={{ xs: 2, sm: 3 }}>
                      <FloatingIcon sx={{ width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
                        <WebIcon />
                      </FloatingIcon>
                      <Typography variant="h5" fontWeight={700} fontSize={{ xs: '1.25rem', sm: '1.5rem' }}>
                        iFrames
                      </Typography>
                    </Stack>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }}>
                      Without prior approval and written permission, you may not create frames around our Webpages that alter in any way the visual presentation or appearance of our Website.
                    </Typography>
                  </CardContent>
                </GlassCard>
              </Slide>

              {/* Content Liability & Other Sections */}
              <Slide in direction="up" timeout={1100}>
                <GlassCard sx={{ mb: { xs: 2, sm: 3 } }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }} mb={{ xs: 2, sm: 3 }}>
                      <FloatingIcon sx={{ width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
                        <WarningIcon />
                      </FloatingIcon>
                      <Typography variant="h5" fontWeight={700} fontSize={{ xs: '1.25rem', sm: '1.5rem' }}>
                        Content Liability
                      </Typography>
                    </Stack>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} paragraph>
                      We shall not be hold responsible for any content that appears on your Website. You agree to protect and defend us against all claims that is rising on your Website. No link(s) should appear on any Website that may be interpreted as libelous, obscene or criminal, or which infringes, otherwise violates, or advocates the infringement or other violation of, any third party rights.
                    </Typography>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} paragraph>
                      <strong>Your Privacy:</strong> Please read Privacy Policy
                    </Typography>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} paragraph>
                      <strong>Reservation of Rights:</strong> We reserve the right to request that you remove all links or any particular link to our Website. You approve to immediately remove all links to our Website upon request. We also reserve the right to amen these terms and conditions and it’s linking policy at any time. By continuously linking to our Website, you agree to be bound to and follow these linking terms and conditions.
                    </Typography>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} paragraph>
                      <strong>Removal of Links from Our Website:</strong> If you find any link on our Website that is offensive for any reason, you are free to contact and inform us any moment. We will consider requests to remove links but we are not obligated to or so or to respond to you directly.
                    </Typography>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }}>
                      We do not ensure that the information on this website is correct, we do not warrant its completeness or accuracy; nor do we promise to ensure that the website remains available or that the material on the website is kept up to date.
                    </Typography>
                  </CardContent>
                </GlassCard>
              </Slide>

              {/* Disclaimer Section */}
              <Slide in direction="up" timeout={1200}>
                <GlassCard>
                  <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }} mb={{ xs: 2, sm: 3 }}>
                      <FloatingIcon sx={{ width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
                        <GavelIcon />
                      </FloatingIcon>
                      <Typography variant="h5" fontWeight={700} fontSize={{ xs: '1.25rem', sm: '1.5rem' }}>
                        Disclaimer
                      </Typography>
                    </Stack>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} paragraph>
                      To the maximum extent permitted by applicable law, we exclude all representations, warranties and conditions relating to our website and the use of this website. Nothing in this disclaimer will:
                    </Typography>
                    <List dense sx={{ mb: 2 }}>
                      {[
                        'limit or exclude our or your liability for death or personal injury',
                        'limit or exclude our or your liability for fraud or fraudulent misrepresentation',
                        'limit any of our or your liabilities in any way that is not permitted under applicable law',
                        'exclude any of our or your liabilities that may not be excluded under applicable law'
                      ].map((item, idx) => (
                        <ListItem key={idx} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}><CancelIcon fontSize="small" color="error" /></ListItemIcon>
                          <ListItemText primary={item} primaryTypographyProps={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }} />
                        </ListItem>
                      ))}
                    </List>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }} paragraph>
                      The limitations and prohibitions of liability set in this Section and elsewhere in this disclaimer: (a) are subject to the preceding paragraph; and (b) govern all liabilities arising under the disclaimer, including liabilities arising in contract, in tort and for breach of statutory duty.
                    </Typography>
                    <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }}>
                      As long as the website and the information and services on the website are provided free of charge, we will not be liable for any loss or damage of any nature.
                    </Typography>
                  </CardContent>
                </GlassCard>
              </Slide>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box id="privacy" sx={{ scrollMarginTop: '100px' }}>
              <GlassCard sx={{ mb: 1 }}>
                <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                  <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }} mb={{ xs: 2, sm: 3 }}>
                    <FloatingIcon bgcolor={alpha('#10b981', 0.1)} sx={{ width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
                      <ShieldIcon sx={{ color: '#10b981' }} />
                    </FloatingIcon>
                    <Box>
                      <Typography variant="h4" fontWeight={700} fontSize={{ xs: '1.4rem', sm: '1.8rem', md: '2rem' }}>
                        Privacy Policy Overview
                      </Typography>
                     
                    </Box>
                  </Stack>
                  <Typography variant="body1" paragraph lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }}>
                    This Privacy Policy applies to the clanbridge.com. Clanbridge.com recognizes the importance of 
                    maintaining your privacy. We value your privacy and appreciate your trust in us.
                  </Typography>
                  <Alert severity="info" sx={{ borderRadius: '20px', mb: 2 }}>
                    By visiting and/or using our website, you agree to this Privacy Policy.
                  </Alert>
                  <Typography variant="body1" fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }}>
                    Clanbridge.com is a property of <strong>Clanbridge Living Private Limited</strong>, an Indian Company 
                    registered under the Companies Act, 2013 having its registered office at Plot Number-18,19 Ecotech Extension-1, Kasna, Greater Noida, G.B.Nagar, 201308, India.
                  </Typography>
                </CardContent>
              </GlassCard>
            </Box>

            <Grid container spacing={{ xs: 2, sm: 3 }} mt={0.5}>
              {[
                { title: 'Information We Collect', icon: <BusinessIcon />, content: 'Contact Information. While signing up, we collect personal information like name, email address and phone number.' },
                { title: 'Use of Your Information', icon: <VerifiedIcon />, content: 'This information is used to contact or identify you, and send you the relevant material for choosing Clanbridge Living.' },
                { title: 'Sharing Of Information with Third Parties', icon: <VerifiedIcon />, content: 'The third-party providers used by us will in general use and disclose your information to the extent necessary to allow them to execute the operation they provide. We recommend that you read the privacy policy of the third-party providers to understand the manner in which your information will be handled by them.' },
                 { title: 'Disclosure', icon: <SecurityIcon />, content: 'We may disclose your information if we are required to do so by law or if you violate our terms and conditions.' },
                  { title: 'Consent', icon: <SecurityIcon />, content: 'When you provide us with personal information, we imply that you give us your consent to collect and use the information for the relevant use cases.' },
                { title: 'Security', icon: <SecurityIcon />, content: 'We use industry best practices to secure your personal information.' },
                { title: 'Age of Consent', icon: <HelpIcon />, content: 'By using our platform, you represent that you are at least the age of majority in your state or province of residence.' },
                { title: 'Third Party Sites', icon: <HelpIcon />, content: 'If you click on one of the links to third party websites, you may be taken to websites we do not control. This policy does not apply to the privacy practices of those websites. Read the privacy' },
                { title: 'Updates to This Policy', icon: <SecurityIcon />, content: 'This Privacy Policy was last updated on 21.06.2023. From time to time we may change our privacy practices. We will notify you of any material changes to this policy as required by law. We will also post an updated copy on our website. Please check our site periodically for updates.' },
                { title: 'Jurisdiction', icon: <SecurityIcon />, content: 'If you choose to visit the website, your visit and any dispute over privacy is subject to this policy and the website’s terms of use. In addition to the foregoing, any disputes arising under this policy shall be governed by the laws of India.' },
              ].map((item, idx) => (
                <Grid item xs={12} md={6} key={idx}>
                  <Zoom in timeout={500 + idx * 100}>
                    <InteractiveCard elevation={0} sx={{ p: { xs: 2, sm: 3 } }}>
                      <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center" mb={2}>
                        <FloatingIcon sx={{ width: { xs: 36, sm: 40 }, height: { xs: 36, sm: 40 } }}>
                          {item.icon}
                        </FloatingIcon>
                        <Typography variant="h6" fontWeight={700} fontSize={{ xs: '1rem', sm: '1.125rem' }}>
                          {item.title}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" fontSize={{ xs: '0.8rem', sm: '0.875rem' }}>
                        {item.content}
                      </Typography>
                    </InteractiveCard>
                  </Zoom>
                </Grid>
              ))}
            </Grid>

            <GlassCard sx={{ mt: { xs: 2, sm: 3 } }}>
              <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Typography variant="h5" fontWeight={700} gutterBottom fontSize={{ xs: '1.25rem', sm: '1.5rem' }}>
                  Grievance Officer
                </Typography>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={{ xs: 1.5, sm: 2 }}>
                                          <Typography fontSize={{ xs: '0.8rem', sm: '0.9rem' }}>In accordance with Information Technology Act 2000 and rules made there under, the name and contact details of the Grievance Officer are provided below:
                                              Mr. Grievance Officer</Typography>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <LocationIcon sx={{ color: '#10b981', fontSize: { xs: 20, sm: 24 }, mt: 0.5 }} />
                      
                        <Typography fontSize={{ xs: '0.8rem', sm: '0.9rem' }}>Plot Number-18,19 Ecotech Extension-1, Kasna, Greater Noida, 201308</Typography>
                      </Stack>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <PhoneIcon sx={{ color: '#10b981', fontSize: { xs: 20, sm: 24 } }} />
                        <Typography fontSize={{ xs: '0.8rem', sm: '0.9rem' }}>+91XXXXXXXXXX</Typography>
                      </Stack>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <EmailIcon sx={{ color: '#10b981', fontSize: { xs: 20, sm: 24 } }} />
                        <Typography fontSize={{ xs: '0.8rem', sm: '0.9rem' }}>grivances@clanbridge.com</Typography>
                      </Stack>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: alpha('#10b981', 0.05), borderRadius: '20px' }}>
                      <Typography variant="body2" fontSize={{ xs: '0.75rem', sm: '0.875rem' }}>
                        <strong>Working Hours:</strong> Monday to Friday, 9:00 AM - 6:00 PM IST
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </GlassCard>
          </TabPanel>


          <TabPanel value={tabValue} index={2}>
            <Box id="refund" sx={{ scrollMarginTop: '100px' }}>
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                {[
                  {
                    title: 'Booking Advance',
                    icon: <PaymentIcon />,
                    content: 'Booking advance to be forfeited after 1 week from the date of booking in case booking is cancelled. If booking is cancelled within 7 days then the booking amount shall be refunded back within 30 days from the booking date.',
                    color: theme.palette.primary.main
                  },
                  {
                    title: 'Security Deposit Refund',
                    icon: <RefundIcon />,
                    content: 'Security deposit shall be refunded as per evaluation of any damages made to the property and its assets and after recovery of any balance dues accruing to the resident within 30 days of refund request.',
                    note: 'The refund request can be made post completion of the contract and check out from the premises by sending an email to sales@clanbridge.com',
                    color: '#ef4444'
                  },
                  {
                    title: 'Cancellation of Tenancy',
                    icon: <CancelIcon />,
                    content: 'In case of cancellation of tenancy contract, advance paid for the balance term shall stand forfeited and any balance pending shall not be refunded.',
                    color: '#f59e0b'
                  }
                ].map((policy, idx) => (
                  <Grid item xs={12} key={idx}>
                    <Slide in direction="up" timeout={500 + idx * 100}>
                      <GlassCard>
                        <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                          <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }} mb={{ xs: 2, sm: 3 }}>
                            <FloatingIcon bgcolor={alpha(policy.color, 0.1)} sx={{ width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
                              {policy.icon}
                            </FloatingIcon>
                            <Typography variant="h5" fontWeight={700} fontSize={{ xs: '1.25rem', sm: '1.5rem' }}>
                              {policy.title}
                            </Typography>
                          </Stack>
                          <Typography variant="body1" lineHeight={1.8} fontSize={{ xs: '0.85rem', sm: '0.95rem', md: '1rem' }}>
                            {policy.content}
                          </Typography>
                          {policy.note && (
                            <Alert severity="warning" sx={{ mt: 3, borderRadius: '20px' }}>
                              {policy.note}
                            </Alert>
                          )}
                        </CardContent>
                      </GlassCard>
                    </Slide>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </TabPanel>
        </Paper>

        <Box sx={{ mt: { xs: 4, sm: 5, md: 6 }, textAlign: 'center' }}>
          <Divider sx={{ mb: 3 }}>
            <Chip icon={<FavoriteIcon />} label="Trusted by thousands" size={isMobile ? "small" : "medium"} />
          </Divider>
         
          <Typography variant="body2" color="text.secondary" fontSize={{ xs: '0.7rem', sm: '0.875rem' }}>
            <CopyrightIcon sx={{ fontSize: { xs: 12, sm: 14 }, verticalAlign: 'middle', mr: 0.5 }} />
            {new Date().getFullYear()} Clanbridge Living Private Limited. All rights reserved.
          </Typography>
        </Box>
      </Container>

      <style>
        {`
          @keyframes float {
            0%, 100% {
              transform: translateY(0px) translateX(0px);
            }
            50% {
              transform: translateY(-20px) translateX(10px);
            }
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .MuiCard-root {
            animation: fadeInUp 0.6s ease-out;
          }
          
          @media print {
            .MuiButton-root, .MuiIconButton-root, .MuiTabs-root, 
            [class*="sticky"], [class*="FloatingIcon"], [class*="InteractiveCard"],
            .MuiAppBar-root {
              display: none !important;
            }
            .MuiContainer-root {
              padding: 0 !important;
              margin: 0 !important;
            }
            .MuiPaper-root {
              box-shadow: none !important;
              border: 1px solid #ddd !important;
            }
            body {
              background: white !important;
            }
          }
          
          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          
          ::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
          }
          
          ::-webkit-scrollbar-thumb {
            background: linear-gradient(135deg, #1e3c72, #2a5298);
            border-radius: 10px;
          }
          
          ::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(135deg, #2a5298, #1e3c72);
          }
        `}
      </style>
    </Box>
  );
}